import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Users,
  ShoppingCart,
  Calendar,
  Shield,
  Home,
  MessageSquare,
  Star,
  Settings,
  TrendingUp,
  DollarSign,
  Package,
  CreditCard,
  FileText,
  Bell,
  HelpCircle,
  Mail
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import AdminPageBanner from '../../components/admin/AdminPageBanner';

const AdminDashboard = () => {
  const { user } = useAuth();

  const quickStats = [
    {
      title: 'Total Users',
      value: '1,234',
      icon: Users,
      color: 'bg-blue-500',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600'
    },
    {
      title: 'Active Subscriptions',
      value: '856',
      icon: CreditCard,
      color: 'bg-green-500',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600'
    },
    {
      title: 'Total Orders',
      value: '2,145',
      icon: ShoppingCart,
      color: 'bg-orange-500',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600'
    },
    {
      title: 'Monthly Revenue',
      value: '$45,230',
      icon: DollarSign,
      color: 'bg-purple-500',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600'
    }
  ];

  const managementLinks = [
    {
      title: 'User Management',
      description: 'Manage users, roles, and permissions',
      icon: Users,
      href: '/admin/users',
      color: 'bg-blue-500',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600'
    },
    /*{
      title: 'Subscription Management',
      description: 'Manage subscriptions and schedule inspections',
      icon: CreditCard,
      href: '/admin/subscriptions',
      color: 'bg-green-500',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600'
    },*/
    {
      title: 'Inspection Management',
      description: 'Manage property inspections and schedules',
      icon: Shield,
      href: '/admin/inspections',
      color: 'bg-cyan-500',
      iconBg: 'bg-cyan-100',
      iconColor: 'text-cyan-600'
    },
    {
      title: 'Email Testing',
      description: 'Test email functionality and SMTP configuration',
      icon: Mail,
      href: '/admin/email-test',
      color: 'bg-purple-500',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600'
    },
    {
      title: 'Order Management',
      description: 'View and manage customer orders',
      icon: ShoppingCart,
      href: '/admin/orders',
      color: 'bg-orange-500',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600'
    },
    {
      title: 'Event Management',
      description: 'Create and manage events',
      icon: Calendar,
      href: '/admin/events',
      color: 'bg-purple-500',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600'
    },
    {
      title: 'CEA Verification',
      description: 'Verify agent credentials',
      icon: Shield,
      href: '/admin/cea-verification',
      color: 'bg-red-500',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600'
    },
    {
      title: 'Homepage Settings',
      description: 'Customize homepage content',
      icon: Home,
      href: '/admin/homepage',
      color: 'bg-indigo-500',
      iconBg: 'bg-indigo-100',
      iconColor: 'text-indigo-600'
    },
    {
      title: 'FAQ Management',
      description: 'Manage frequently asked questions',
      icon: HelpCircle,
      href: '/admin/faqs',
      color: 'bg-teal-500',
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600'
    },
    {
      title: 'Announcements',
      description: 'Create and manage announcements',
      icon: Bell,
      href: '/admin/announcements',
      color: 'bg-yellow-500',
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-600'
    },
    {
      title: 'Review Management',
      description: 'Manage customer reviews and ratings',
      icon: Star,
      href: '/admin/reviews',
      color: 'bg-pink-500',
      iconBg: 'bg-pink-100',
      iconColor: 'text-pink-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-orange-50">
      <AdminPageBanner
        title="Admin Dashboard"
        subtitle={`Welcome back, ${user?.firstName}! Manage your platform from here`}
        icon={Settings}
        iconLabel="Admin Panel"
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {quickStats.map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 ${stat.iconBg} rounded-lg flex items-center justify-center`}>
                  <stat.icon className={`w-6 h-6 ${stat.iconColor}`} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Management Links */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Management Tools</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {managementLinks.map((link, index) => (
              <motion.div
                key={link.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 + index * 0.05 }}
              >
                <Link
                  to={link.href}
                  className="block bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-lg hover:border-orange-200 transition-all group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 ${link.iconBg} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <link.icon className={`w-6 h-6 ${link.iconColor}`} />
                    </div>
                    <div className="text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                    {link.title}
                  </h3>
                  <p className="text-sm text-gray-600">{link.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/admin/users"
              className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg hover:shadow-md transition-shadow group"
            >
              <Users className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-900 group-hover:text-blue-700">Add New User</span>
            </Link>
            <Link
              to="/admin/subscriptions"
              className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-lg hover:shadow-md transition-shadow group"
            >
              <Calendar className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-900 group-hover:text-green-700">Schedule Inspection</span>
            </Link>
            <Link
              to="/admin/announcements"
              className="flex items-center gap-3 p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg hover:shadow-md transition-shadow group"
            >
              <Bell className="w-5 h-5 text-orange-600" />
              <span className="text-sm font-medium text-orange-900 group-hover:text-orange-700">Create Announcement</span>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboard;
